import React, { useState } from 'react';

import { ActionIcon, Group, Popover, Text, UnstyledButton } from '@mantine/core';
import { DatePicker, DateValue } from '@mantine/dates';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import dayjs from 'dayjs';

import classes from './assignToDatePill.module.css';

interface AssignToDatePillProps {
  date: string;
  onChange: (date: string) => void;
}

const AssignToDatePill = ({ date, onChange }: AssignToDatePillProps): JSX.Element => {
  const [pickerOpened, setPickerOpened] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(() => dayjs(date).toDate());

  const step = (amount: number): void => {
    const next = dayjs(date).add(amount, 'day');

    setVisibleMonth(next.toDate());
    onChange(next.format('YYYY-MM-DD'));
  };

  const togglePicker = (): void => {
    setVisibleMonth(dayjs(date).toDate());
    setPickerOpened(opened => !opened);
  };

  const handlePick = (value: DateValue): void => {
    if (!value) return;

    onChange(dayjs(value).format('YYYY-MM-DD'));
    setPickerOpened(false);
  };

  const closePickerOnly = (event: React.KeyboardEvent): void => {
    if (event.key !== 'Escape' || !pickerOpened) return;

    event.stopPropagation();
    setPickerOpened(false);
  };

  return (
    <Group spacing='xs' noWrap data-cy='assignToDatePill' onKeyDown={closePickerOnly}>
      <Text size='xs' fw={600} color='dimmed' tt='uppercase'>
        Assign to date
      </Text>
      <ActionIcon onClick={() => step(-1)} variant='subtle' color='kiloBlue' aria-label='Previous day'>
        <IconChevronLeft size={16} />
      </ActionIcon>
      <Popover opened={pickerOpened} onChange={setPickerOpened} position='bottom' withinPortal shadow='md'>
        <Popover.Target>
          <UnstyledButton className={classes.pill} onClick={togglePicker} data-cy='assignToDateValue'>
            <Text size='sm' fw={600}>
              {dayjs(date).format('MMMM D, YYYY')}
            </Text>
          </UnstyledButton>
        </Popover.Target>
        <Popover.Dropdown>
          <DatePicker
            value={dayjs(date).toDate()}
            date={visibleMonth}
            onDateChange={setVisibleMonth}
            onChange={handlePick}
          />
        </Popover.Dropdown>
      </Popover>
      <ActionIcon onClick={() => step(1)} variant='subtle' color='kiloBlue' aria-label='Next day'>
        <IconChevronRight size={16} />
      </ActionIcon>
    </Group>
  );
};

export default AssignToDatePill;
